import { theme } from '@arnold/common';
import styled from '@emotion/styled';

export const Main = styled.main`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

export const Content = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  overflow-anchor: none;
  display: flex;
  flex-direction: column;

  /* Only the body may grow, which keeps the footer at the bottom of short pages. Nothing may shrink: the header
     slot's only child is absolutely positioned, so as a shrinkable flex item it would collapse to nothing. */
  > * {
    flex-shrink: 0;
  }
`;

export const Body = styled.div`
  flex-grow: 1;
  padding: ${theme.spacing.g};
`;

/** Sticks to the top of the scroller and reserves the header's expanded height in the layout, so the header
 * can collapse without shortening the scrollable content. Pointer events pass through the uncovered part. */
export const HeaderSlot = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  height: var(--new-report-header-offset, auto);
  pointer-events: none;
`;

export const Header = styled.header<{ compact?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  pointer-events: auto;
  background-color: ${theme.colors.backgroundCover.default};
  box-shadow: 0px 2px 4px 0px #00000014;
  padding: ${(props) => (props.compact ? theme.spacing.e : theme.spacing.g)} ${theme.spacing.g};
  transition: padding 0.2s ease;

  @media (max-width: ${theme.breakpoint.small}) {
    padding: ${theme.spacing.f};
  }
`;

export const HeaderRow = styled.div<{ compact?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: ${(props) => (props.compact ? 0 : theme.spacing.d)};
`;

export const HeaderRowContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.h};
`;

export const UserMenuContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.d};
  min-width: 94px;
`;

export const Title = styled.h1<{ compact?: boolean }>`
  margin: 0;
  font-family: 'Roboto';
  color: ${theme.colors.text.primary};
  line-height: ${theme.typography.heading.large.default.lineHeight};
  transition: font-size 0.2s ease;
  font-size: ${(props) =>
    props.compact ? theme.typography.body.medium.regular.fontSize : theme.typography.heading.large.default.fontSize};
  font-weight: ${(props) =>
    props.compact ? theme.typography.body.medium.bold.fontWeight : theme.typography.heading.large.bold.fontWeight};
`;

export const CollapsibleInfo = styled.div<{ compact?: boolean }>`
  display: grid;
  grid-template-rows: ${(props) => (props.compact ? '0fr' : '1fr')};
  opacity: ${(props) => (props.compact ? 0 : 1)};
  overflow: hidden;
  transition:
    grid-template-rows 0.25s ease,
    opacity 0.2s ease;
`;

export const CollapsibleInfoInner = styled.div`
  min-height: 0;
`;

export const InfoContainer = styled.div`
  padding: ${theme.spacing.g} 0;
`;

export const InfoRow = styled.div`
  display: flex;
  gap: ${theme.spacing.f};
  flex-wrap: wrap;
  padding-bottom: ${theme.spacing.g};
`;

export const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.c};
  min-width: 0;

  svg {
    min-width: 22px;
  }

  path {
    fill: ${theme.colors.text.secondary};
  }
`;

export const RootTeamsValue = styled.span`
  display: flex;
  align-items: flex-start;
  min-width: 0;
  width: 100%;
`;

export const RootTeamsText = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
`;

export const RootTeamsExpand = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  white-space: nowrap;
`;

export const RootTeamsToggle = styled.button`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  border: 0;
  padding: 0;
  margin-left: ${theme.spacing.c};
  background: transparent;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  vertical-align: middle;
  white-space: nowrap;

  path {
    fill: ${theme.colors.iconAction.primary};
  }
`;

export const Separator = styled.div`
  height: 1px;
  background-color: ${theme.colors.borderSeparator.default};
`;
